import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import NotFound from './NotFound';
import ForgotPassword from './ForgotPassword';
import SharedProject from './SharedProject';
import ResetPassword from './ResetPassword';
import AdminDashboard from './AdminDashboard';
import Navbar from './Navbar';
import LandingPage from './LandingPage';
import LoginPage from './LoginPage';
import RegisterPage from './RegisterPage';
import LatexEditor from './LatexEditor';
import ConvertPage from './ConvertPage';
import PricingPage from './PricingPage';
import WordToLatexPage from './WordToLatexPage';
import TermsOfService from './legal/TermsOfService';
import PrivacyPolicy from './legal/PrivacyPolicy';
import About from './legal/About';
import './App.css';

function AppContent() {
  const location = useLocation();
  const isLandingPage = location.pathname === '/' || location.pathname === '';
  const [user, setUser] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSending, setFeedbackSending] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    fetch(`${API}/api/auth/me`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => {
        if (data.success && data.user) {
          localStorage.setItem('user', JSON.stringify(data.user));
          setUser(data.user);
        } else {
          // Token invalid/expired — clear stale session
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      })
      .catch(() => {
        // Network error: fall back to cached user so the app stays usable offline
        const userData = localStorage.getItem('user');
        if (userData) setUser(JSON.parse(userData));
      });
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    window.location.href = '/';
  };

  const handleLoginSuccess = (userData) => {
    setUser(userData);
  };

  const handleFeedbackClick = () => {
    setShowFeedback(true);
  };

  const handleFeedbackClose = () => {
    setShowFeedback(false);
  };

  const handleSendFeedback = async () => {
    if (!feedbackText.trim()) return;
    setFeedbackSending('sending');
    try {
      const response = await fetch(process.env.REACT_APP_API_URL + '/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          feedback: feedbackText,
          userEmail: user?.email || 'anonymous',
          timestamp: new Date().toISOString()
        })
      });
      const data = await response.json();
      if (data.success) {
        setFeedbackSending('done');
        setFeedbackText('');
        setTimeout(() => { setShowFeedback(false); setFeedbackSending(''); }, 1500);
      } else {
        setFeedbackSending('error');
      }
    } catch (err) {
      setFeedbackSending('error');
    }
  };

  return (
    <div className="App">
      {!isLandingPage && (
        <Navbar
          user={user}
          onLogout={handleLogout}
          onFeedbackClick={handleFeedbackClick}
        />
      )}
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/about" element={<About />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/login" element={user ? <Navigate to="/editor" /> : <LoginPage onLoginSuccess={handleLoginSuccess} />} />
        <Route path="/register" element={user ? <Navigate to="/editor" /> : <RegisterPage onLoginSuccess={handleLoginSuccess} />} />
        <Route path="/editor" element={<LatexEditor user={user} showFeedback={showFeedback} onFeedbackClose={handleFeedbackClose} onLoginSuccess={handleLoginSuccess} />} />
        <Route path="/convert" element={<ConvertPage user={user} />} />
        <Route path="/word-to-latex" element={<WordToLatexPage />} />
        <Route path="/pricing" element={<PricingPage user={user} />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/shared/:token" element={<SharedProject />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      {showFeedback && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999}}>
          <div style={{background:'white',padding:'30px',borderRadius:'12px',width:'90%',maxWidth:'500px'}}>
            <h2 style={{marginBottom:'20px',color:'#1e293b'}}>📢 Send Feedback</h2>
            {feedbackSending === 'done' ? (
              <p style={{color:'green',textAlign:'center',fontSize:'18px'}}>✅ Thank you for your feedback!</p>
            ) : (
              <>
                <textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder="Tell us what you like, what's missing, or report bugs..."
                  style={{width:'100%',minHeight:'150px',padding:'15px',border:'2px solid #e2e8f0',borderRadius:'8px',fontSize:'14px',fontFamily:'inherit',resize:'vertical',boxSizing:'border-box'}}
                />
                {feedbackSending === 'error' && <p style={{color:'red'}}>Failed to send. Please try again.</p>}
                <div style={{display:'flex',gap:'10px',marginTop:'20px'}}>
                  <button onClick={handleSendFeedback} disabled={feedbackSending==='sending'} style={{flex:1,padding:'12px',background:'#667eea',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontWeight:'bold'}}>
                    {feedbackSending === 'sending' ? '📤 Sending...' : '📤 Send Feedback'}
                  </button>
                  <button onClick={() => { setShowFeedback(false); setFeedbackText(''); setFeedbackSending(''); }} style={{padding:'12px 20px',background:'#f1f5f9',color:'#64748b',border:'none',borderRadius:'8px',cursor:'pointer'}}>Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
